import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useContext } from 'react';
import { AuthContext } from '../../../AuthContext/Authcontext';
import ReusableTable from '../../../Layout/ReusableTable';
import baseURL from "../../../Apiurs/Apiurls";
import Navbar from "../../../Shared/AssociateNavbar/Navbar";
import { FaEye, FaSave, FaEdit, FaTrash, FaComment, FaTable, FaTh , FaHistory,FaEnvelope  } from "react-icons/fa";
import { KanbanBoard } from './kanban';
import { RiFileList2Line } from 'react-icons/ri';


function SalesAssociateOppLeads() { 
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState([]);
  const [message, setMessage] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedAssociate, setSelectedAssociate] = useState({});
  const [assignedData, setAssignedData] = useState({});
  const [associateChanged, setAssociateChanged] = useState({});
  const [oppStatusChanged, setOppStatusChanged] = useState({});
  const [selectedOppStatus, setSelectedOppStatus] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'kanban'

  const navigate = useNavigate();
  const { authToken, userId, userRole } = useContext(AuthContext);

  // Fetch team members (including self)
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/employees/managers`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        // Find the logged-in manager's team
        const manager = response.data.data.find(m => m.id.toString() === userId.toString()) || {};
        const members = manager.teamMembers || [];
        // Include self as an option
        const self = { id: userId, name: 'Self' };
        setTeamMembers([self, ...members]);
      } catch (error) {
        console.error('Error fetching team members:', error);
        setMessage('Failed to fetch team members');
      }
    };
    fetchTeamMembers();
  }, [authToken, userId]);

  // Fetch leads and assignments
  const fetchLeads = async () => {
    try {
      // Fetch leads for the specific userId
      const leadsResponse = await axios.get(`${baseURL}/api/sales-contacts/qualified/${userId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Fetch assignments
      const assignmentsResponse = await axios.get(`${baseURL}/api/lead-assignments`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (leadsResponse.status === 200) {
        const leads = leadsResponse.data;
        setData(leads);
        setFilteredData(leads);

        const associateSelections = {};
        const statusSelections = {};
        const assignments = assignmentsResponse.data;

        leads.forEach((lead) => {
          const assignment = assignments[lead.id] || {};
          associateSelections[lead.id] = assignment.associate_id?.toString() || lead.assigned_to?.toString() || '';
          statusSelections[lead.id] = lead.opp_status;
        });

        setSelectedAssociate(associateSelections);
        setSelectedOppStatus(statusSelections);
        setAssignedData(assignments);
      }
    } catch (error) {
      console.error('Error fetching leads or assignments:', error);
      setMessage(error.response?.data?.message || 'Failed to fetch leads or assignments');
    }
  };

  // Fetch leads and assignments initially
  useEffect(() => {
    if (userId) {
      fetchLeads();
    }
  }, [authToken, userId, teamMembers.length]);

  const handleOppStatusUpdate = async (leadId, newStatus = null) => {
    try {
      const statusToUpdate = newStatus || selectedOppStatus[leadId];

      const response = await axios.put(
        `${baseURL}/api/contacts/${leadId}/opp-status`,
        { opp_status: statusToUpdate },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      setMessage('Opportunity status updated successfully');
      
      if (!newStatus) {
        setOppStatusChanged((prev) => ({ ...prev, [leadId]: false }));
      }

      // Update table data
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, opp_status: statusToUpdate } : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, opp_status: statusToUpdate } : lead
        )
      );
      
      if (!newStatus) {
        fetchLeads();
      }
    } catch (error) {
      console.error('Error updating opportunity status:', error);
      setMessage(error.response?.data?.message || 'Failed to update opportunity status');
      throw error;
    }
  };

  // Handle drop event in Kanban columns
  const handleDrop = async (leadId, newStatus) => {
    try {
      await handleOppStatusUpdate(leadId, newStatus);
    } catch (error) {
      console.error('Error updating status via drag and drop:', error);
      setMessage('Failed to update status via drag and drop');
    }
  };

  // Associate assignment handler for table view (associates typically don't reassign)
  const handleAssociateAssignment = async (leadId) => {
    try {
      const assignedTo = selectedAssociate[leadId];
      if (!assignedTo) {
        setMessage('Please select an associate');
        return;
      }

      const response = await axios.put(
        `${baseURL}/api/leadcrm/${leadId}/assign-user`,
        {
          assigned_by: userId,
          assigned_to: assignedTo,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setMessage('Associate assignment saved successfully');
      setAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
      setAssignedData((prev) => ({
        ...prev,
        [leadId]: response.data.data,
      }));
      
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: assignedTo }
            : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: assignedTo }
            : lead
        )
      );
    } catch (error) {
      console.error('Error assigning associate:', error);
      setMessage(error.response?.data?.message || 'Failed to save associate assignment');
    }
  };

  // Handle edit action
  const handleEdit = (row) => {
    navigate(`/a-edit-lead/${row.id}`);
  };

  // Handle delete action
  const handleDelete = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await axios.delete(`${baseURL}/api/contacts/${leadId}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setMessage('Lead deleted successfully');
        fetchLeads();
      } catch (error) {
        console.error('Error deleting lead:', error);
        setMessage(error.response?.data?.message || 'Failed to delete lead');
      }
    }
  };

  // Handle view action
  const handleView = (lead) => {
    navigate(`/s-view-lead/${lead.id}`, { state: { lead } });
  };

    const opportunity_view = (lead) => {
    navigate(`/s-opportunity_view/${lead.id}`, { state: { lead } });
  };
  // Handle comment action
  const handleComment = (lead) => {
    navigate(`/s-opp-comments/${lead.id}`, { state: { leadid: lead.id } });
  };

  // Reusable truncate cell
  const TruncateCell = ({ value, maxLength = 20 }) => {
    if (value === null || value === undefined) {
      return <span>-</span>;
    }
    const str = String(value);
    return (
      <div
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: `${maxLength * 8}px`,
        }}
        title={str}
      >
        {str.length > maxLength ? str.slice(0, maxLength) + '...' : str}
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      key: 'leadId',
      title: 'LeadID',
      render: (row) => <TruncateCell value={`Lead00${row.id}`} maxLength={10} />,
    },
  {
  key: 'lead_name',
  title: 'Lead Name',
  render: (row) => (
    <span
      className="text-primary"
      style={{ cursor: "pointer",  }}
      onClick={() => handleView(row)}
    >
      <TruncateCell value={row.lead_name} maxLength={15} />
    </span>
  ),
},

    {
      key: 'lead_source',
      title: 'Source',
      render: (row) => <TruncateCell value={row.lead_source} maxLength={12} />,
    },
    {
      key: 'contact_number',
      title: 'Contact Number',
      render: (row) => <TruncateCell value={row.contact_number} maxLength={12} />,
    },
    {
      key: 'email',
      title: 'Email',
      render: (row) => <TruncateCell value={row.email} maxLength={20} />,
    },
    {
      key: 'quotation_status',
      title: 'Quotation Status',
      render: (row) => {
        const status = row.quotation_status || 'Not Quoted';
        let badgeClass = '';
        
        switch (status.toLowerCase()) {
          case 'sent':
            badgeClass = 'badge bg-success';
            break;
          case 'approved':
            badgeClass = 'badge bg-secondary';
            break;
          case 'rejected':
            badgeClass = 'badge bg-danger';
            break;
          case 'accepted':
            badgeClass = 'badge bg-info';
            break;
          default:
            badgeClass = 'badge bg-light text-dark';
        }
        
        return (
          <span className={badgeClass} style={{ fontSize: '0.75rem', padding: '0.35em 0.65em' }}>
            {status}
          </span>
        );
      },
    },
    {
      key: 'opp_status',
      title: 'Opp Status',
      render: (row) => {
        const leadId = row.id;
        return (
          <div className="d-flex align-items-center gap-2" style={{ minWidth: '200px' }}>
            <select
              className="form-select form-select-sm"
              value={selectedOppStatus[leadId] || 'Prospecting'}
              onChange={(e) => {
                const newStatus = e.target.value;
                setSelectedOppStatus((prev) => ({ ...prev, [leadId]: newStatus }));
                setOppStatusChanged((prev) => ({ ...prev, [leadId]: true }));
              }}
            >
              <option value="Prospecting">Prospecting</option>
              <option value="Proposal Sent">Proposal Sent</option>
              <option value="Negotiation">Negotiation</option>
              <option value="Regret">Regret</option>
              <option value="Closed Won">Closed Won</option>
              <option value="Closed Lost">Closed Lost</option>
              <option value="On Hold">On Hold</option>
            </select>
            <button
              className="btn btn-sm btn-success"
              onClick={() => handleOppStatusUpdate(leadId)}
              disabled={!oppStatusChanged[leadId]}
              title="Save Opp Status"
            >
              <FaSave />
            </button>
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (row) => (
        <div className="d-flex gap-2">
          {/* <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleView(row)}
            title="View"
          >
            <FaEye />
          </button> */}
         
            {/* <button
                     className="btn btn-sm btn-outline-primary"
                     onClick={() => handleView(row)}
                     title="View"
                   >
                     <RiFileList2Line  />
                   </button> */}
                      <button
                     className="btn btn-sm btn-outline-primary"
                
                 onClick={() => opportunity_view(row)}
             
                     title="View-Edit"
                   >
                     <FaEye  />
                   </button>

          
          {/* <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleEdit(row)}
            title="Edit"
          >
            <FaEdit />
          </button> */}
          {/* <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row.id)}
            title="Delete"
          >
            <FaTrash />
          </button> */}
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleComment(row)}
            title="Comment"
          >
            <FaComment />
          </button>

                        <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate(`/s-view-sent-quotations/${row.id}`, { state: { leadid: row.id } })}
                      title="History"
                    >
                      <FaHistory />
                    </button>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() =>
                          navigate(`/s-send-email/${row.id}`, {
                            state: { leadid: row.id, email: row.email },
                          })
                        }
                        title="Send Email"
                      >
                        <FaEnvelope />
                      </button>
        </div>
      ),
    },
  ];

  return (
    <div className="AssociateLeadscontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`AssociateLeads ${collapsed ? 'collapsed' : ''}`}>
        <div className="AssociateLeads-container mb-5">
          <div className="AssociateLeads-table-container">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>Associate Opportunity Leads</h4>
              <div className="view-mode-buttons">
                <button
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('table')}
                >
                  <FaTable /> Table View
                </button>
                <button
                  className={`btn ms-2 ${viewMode === 'kanban' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('kanban')}
                >
                  <FaTh /> Kanban View
                </button>
              </div>
            </div>

            {message && (
              <div className={`alert ${message.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
                {message}
              </div>
            )}

            {viewMode === 'table' ? (
              <ReusableTable
                data={filteredData}
                columns={columns}
                initialEntriesPerPage={5}
                searchPlaceholder="Search leads..."
              />
            ) : (
              <KanbanBoard
                leads={filteredData}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onComment={handleComment}
                onStatusUpdate={handleOppStatusUpdate}
                onDrop={handleDrop}
                userId={userId}
              />
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .kanban-board {
          overflow-x: auto;
          padding: 10px;
          background: #f5f5f5;
          min-height: 80vh;
        }
        
        .kanban-columns-container {
          display: flex;
          gap: 15px;
          min-width: max-content;
        }
        
        .kanban-column {
          min-width: 320px;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 10px;
          border: 2px dashed transparent;
          transition: all 0.3s ease;
        }
        
        .kanban-column.drag-over {
          border-color: #007bff;
          background: #e3f2fd;
        }
        
        .kanban-column-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: white;
          border-radius: 6px;
          margin-bottom: 10px;
          font-weight: bold;
        }
        
        .kanban-column-body {
          min-height: 600px;
          transition: background-color 0.2s;
        }
        
        .kanban-column-body.drag-over {
          background-color: #e3f2fd;
        }
        
        .kanban-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          overflow: hidden;
          margin-bottom: 10px;
          transition: transform 0.2s;
        }
        
        .kanban-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .kanban-card:active {
          cursor: grabbing;
        }
        
        .kanban-card-header {
          padding: 12px;
          color: black;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: bold;
        }
        
        .kanban-status-badge {
          background: rgba(0,0,0,0.2);
          padding: 4px 12px;
          border-radius: 15px;
          font-size: 0.8em;
          font-weight: bold;
        }
        
        .kanban-card-body {
          padding: 12px;
        }
        
        .kanban-field {
          margin-bottom: 8px;
          font-size: 0.9em;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .kanban-field-group {
          margin-bottom: 10px;
        }
        
        .kanban-field-group label {
          font-size: 0.85em;
          margin-bottom: 4px;
          display: block;
        }
        
        .kanban-field strong {
          margin-right: 5px;
          min-width: 60px;
        }
        
        .kanban-actions {
          border-top: 1px solid #eee;
          padding-top: 12px;
        }
        
        .kanban-empty-state {
          text-align: center;
          padding: 20px;
          color: #6c757d;
          font-style: italic;
        }
        
        .view-mode-buttons {
          display: flex;
          gap: 10px;
        }
      `}</style>
    </div>
  );
}

export default SalesAssociateOppLeads;